export interface GoogleTokenClient {
  requestAccessToken: (opts?: { prompt?: string }) => void;
}

export interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
}

export interface FacebookAuthResponse {
  accessToken: string;
}

export interface FacebookLoginResponse {
  authResponse?: FacebookAuthResponse | null;
  status?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
          }) => GoogleTokenClient;
        };
      };
    };
    FB?: {
      init: (config: {
        appId: string;
        cookie?: boolean;
        xfbml?: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: FacebookLoginResponse) => void,
        options: { scope: string }
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

const GOOGLE_SDK_SRC = 'https://accounts.google.com/gsi/client';
const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

let googleSdkPromise: Promise<void> | null = null;
let facebookSdkPromise: Promise<void> | null = null;

export function loadGoogleSdk(): Promise<void> {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }
  if (googleSdkPromise) {
    return googleSdkPromise;
  }
  googleSdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GOOGLE_SDK_SRC}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google sign-in')));
      if (window.google?.accounts?.oauth2) {
        resolve();
      }
      return;
    }
    const script = document.createElement('script');
    script.src = GOOGLE_SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google sign-in'));
    document.head.appendChild(script);
  });
  return googleSdkPromise;
}

export function loadFacebookSdk(appId: string): Promise<void> {
  if (window.FB) {
    return Promise.resolve();
  }
  if (facebookSdkPromise) {
    return facebookSdkPromise;
  }
  facebookSdkPromise = new Promise((resolve, reject) => {
    const finish = () => {
      window.FB?.init({
        appId,
        cookie: true,
        xfbml: false,
        version: 'v21.0',
      });
      resolve();
    };
    if (window.FB) {
      finish();
      return;
    }
    window.fbAsyncInit = finish;
    if (document.getElementById('facebook-jssdk')) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = FACEBOOK_SDK_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Facebook sign-in'));
    document.body.appendChild(script);
  });
  return facebookSdkPromise;
}

export function socialOauthStartUrl(provider: string): string {
  const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
  return `/api/auth/social/start/${encodeURIComponent(provider)}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export function hasPendingSocialAuthError(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return !!params.get('social_error') || params.get('error') === 'social';
}

export function consumeSocialAuthError(): string | null {
  const params = new URLSearchParams(window.location.search);
  if (!params.get('social_error') && params.get('error') !== 'social') {
    return null;
  }
  params.delete('social_error');
  if (params.get('error') === 'social') {
    params.delete('error');
  }
  const next = params.toString();
  const url = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', url);
  return 'Google or Facebook sign-in failed. Try again, or continue with email / OTP.';
}
